/**
 * NA-313: the override rules for a Question Bank-owned field a block can take
 * ownership of, in one place.
 *
 * Field names are parameters rather than branched on internally, because the two
 * block types name the same concept differently (`correctExplanation` vs
 * `correctAnswerExplanation`) — fixing one and missing the other is the likeliest
 * mistake in this feature.
 *
 * Two states that are easy to conflate:
 * - **overridden** is STICKY: once set, the block stops tracking the bank.
 * - **diverged** is COMPUTED: the block's value differs from the bank value for
 *   the locale being viewed.
 *
 * They are deliberately not the same. Overriding with text identical to the bank
 * leaves the block overridden but not diverged (no badge), and if the bank value
 * later changes the badge appears with no author action. The indicator tracks
 * divergence; persistence tracks ownership.
 */

import { useCallback, useMemo } from 'react';
import type { JSONContent } from '@tiptap/core';

export interface FieldOverrideNames<TBlock> {
  /** The authored value key, e.g. `correctExplanation`. */
  value: keyof TBlock & string;
  /** The ownership marker key, e.g. `correctExplanationOverridden`. */
  marker: keyof TBlock & string;
  /** The read-only bank comparison value supplied by resolution. */
  bankValue: keyof TBlock & string;
  /**
   * When true, an absent bank value never counts as divergence, even if the
   * block's own value is non-empty — for a field that must always resolve to
   * something (e.g. title, required on Single Choice/Categorization), unlike
   * an optional field where "authored on the block, absent on the bank" is
   * itself meaningful divergence. Defaults to false, preserving the existing
   * description/explanation behavior.
   */
  requireBankValue?: boolean;
}

export interface FieldOverrideState<TValue> {
  /** True when this block has taken ownership of the field. */
  isOverridden: boolean;
  /** True when the block's value differs from the bank value for this locale. */
  isDiverged: boolean;
  /** The value to display: the block's own when overridden, else the bank's. */
  displayValue: TValue | undefined;
  /** Applies an edit: sets the value and claims ownership in one change. */
  buildEdit: (content: TValue) => Record<string, unknown>;
  /** Discards the override by removing both keys — absent means inheriting. */
  buildRevert: () => Record<string, unknown>;
}

/**
 * Structural comparison; both sides are rich-text documents, plain strings, or
 * undefined.
 *
 * Object keys are sorted before stringifying because the two sides reach us
 * through storage that disagrees about key order. The bank value lives in
 * `authorAIQuestionAnswerOptions.correctAnswerExplanation`, a TEXT column that
 * returns the document exactly as TipTap wrote it (`{"type":"text","text":…}`).
 * The block's own value lives in `authorAIBlockContent.payload`, a MySQL JSON
 * column, which normalizes key order by length then bytes and hands it back as
 * `{"text":…,"type":"text"}`.
 *
 * Comparing raw `JSON.stringify` output therefore reports divergence for two
 * identical documents as soon as the override has been reloaded once — the
 * field looks un-diverged until a refresh, then sprouts an "Edited" badge on
 * its own. Array order stays significant: it is document order.
 *
 * NA-384: title is a plain string on both sides, with no such key-ordering
 * concern — `canonicalize` already passes a non-object value through
 * unchanged, so this needs no title-specific branch; two equal strings
 * stringify identically regardless.
 */
const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value === null || typeof value !== 'object') return value;
  const source = value as Record<string, unknown>;
  return Object.keys(source)
    .sort()
    .reduce<Record<string, unknown>>((result, key) => {
      result[key] = canonicalize(source[key]);
      return result;
    }, {});
};

const sameContent = (left: unknown, right: unknown): boolean =>
  JSON.stringify(canonicalize(left ?? null)) === JSON.stringify(canonicalize(right ?? null));

/**
 * NA-384: generalized to a value type parameter so title (a plain string) can
 * reuse this hook unchanged, alongside the existing rich-text fields — see the
 * `TValue` default below, which keeps every pre-NA-384 call site's inferred
 * type exactly as it was without touching them.
 */
export function useFieldOverrideState<TBlock extends object, TValue = JSONContent>(
  block: TBlock,
  names: FieldOverrideNames<TBlock>,
): FieldOverrideState<TValue> {
  const record = block as unknown as Record<string, unknown>;
  const value = record[names.value] as TValue | undefined;
  const bankValue = record[names.bankValue] as TValue | undefined;
  const isOverridden = record[names.marker] === true;

  const isDiverged = useMemo(
    // Only an overridden block can diverge: while inheriting, the displayed
    // value IS the bank value. Comparing content means an override typed back to
    // match the bank stops being reported as diverged.
    //
    // requireBankValue additionally guards against comparing to a bank value
    // that was never supplied — resolution omits it entirely rather than
    // sending an empty one when there's nothing to compare against. For an
    // optional field like correctExplanation, authoring one where the bank
    // has none is still meaningful divergence (NA-313's own design), and
    // reverting it safely lands back on "no explanation" either way. Title
    // opts in because it's required (non-optional) on Single Choice and
    // Categorization, and NA-384's freeze migration sets every pre-existing
    // block's titleOverridden unconditionally, regardless of whether its
    // bank question has a title — without this guard, every such block
    // reports "diverged" from a bank value that doesn't exist, and clicking
    // its Revert clears the block's only title with nothing to restore it
    // from, since resolution has nothing to re-supply on the next read.
    () => isOverridden && !(names.requireBankValue && bankValue === undefined) && !sameContent(value, bankValue),
    [bankValue, isOverridden, names.requireBankValue, value],
  );

  const buildEdit = useCallback(
    (content: TValue): Record<string, unknown> => ({
      [names.value]: content,
      // Written together with the value, never on its own, so a block is never
      // left marked-without-value or value-without-marker.
      [names.marker]: true,
    }),
    [names.marker, names.value],
  );

  const buildRevert = useCallback(
    (): Record<string, unknown> => ({
      // Undefined rather than the bank value: resolution re-supplies the bank
      // value on the next read, and "absent" is the single representation of
      // inheriting. Setting the marker false would create a second one.
      [names.value]: undefined,
      [names.marker]: undefined,
    }),
    [names.marker, names.value],
  );

  return {
    isOverridden,
    isDiverged,
    displayValue: isOverridden ? value : bankValue ?? value,
    buildEdit,
    buildRevert,
  };
}
